/**
 * (Code snippet from https://medium.com/@almestaadmicadiab/how-to-parse-google-maps-address-components-geocoder-response-774d1f3375d)
 * Turns address_components data from <ReactGoogleAutcomplete> into usable data
 *
 * @param googleAddressComponents - address_components from Google Maps API
 * @returns address object with home #, street, city, etc.
 */
export const parseGoogleAddress = (googleAddressComponents: google.maps.GeocoderAddressComponent[]) => {
  const usedTerms: { [key: string]: string } = {};

  const ourTermToGoogleTermMapping: {
    [term: string]: string[];
  } = {
    home: ["street_number"],
    postal_code: ["postal_code"],
    street: ["street_address", "route"],
    region: [
      "administrative_area_level_1",
      "administrative_area_level_2",
      "administrative_area_level_3",
      "administrative_area_level_4",
      "administrative_area_level_5",
    ],
    city: [
      "locality",
      "sublocality",
      "sublocality_level_1",
      "sublocality_level_2",
      "sublocality_level_3",
      "sublocality_level_4",
      "postal_town",
    ],
    country: ["country"],
  }; // Maps address terms that we use to the terms that Google Maps API uses (the first terms in the array have the most priority, the last terms have the least priority)

  const address: { [key: string]: string } = {
    home: "",
    postal_code: "",
    street: "",
    region: "",
    region_short: "",
    city: "",
    country: "",
    full_street: "", // home + street
  };

  const bothNames = ["region"]; // Use both short and long names for these fields
  const shortNames = ["country", "street"]; // Use short names for these fields, long names for the rest

  // Parse google maps address components and map them to our address object
  googleAddressComponents.forEach((googleComponent: google.maps.GeocoderAddressComponent) => {
    for (const term in ourTermToGoogleTermMapping) {
      const googleTerm = googleComponent.types[0];
      const googleTerms = ourTermToGoogleTermMapping[term];
      if (googleTerms.indexOf(googleTerm) === -1) continue;

      // Check if term is already used
      if (usedTerms[term]) {
        const usedGoogleTerm = usedTerms[term];
        if (googleTerms.indexOf(usedGoogleTerm) < googleTerms.indexOf(googleTerm)) {
          continue; // Skip if the current term is less specific than the term that was already used
        }
      }

      // Set the value of the address object
      const shortName = googleComponent.short_name?.replaceAll(".", "");
      if (bothNames.includes(term)) {
        address[term] = googleComponent.long_name;
        address[`${term}_short`] = shortName;
      } else if (shortNames.includes(term)) {
        address[term] = shortName;
      } else {
        address[term] = googleComponent.long_name;
      }
      usedTerms[term] = googleTerm; // Mark term as used
    }
  });

  // Build full street address (home + street)
  if (address.home && address.street) {
    address["full_street"] = `${address.home} ${address.street}`;
  } else if (address.street) {
    address["full_street"] = address.street;
  } else {
    address["full_street"] = "";
  }

  return address;
};
